import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import MasterImportDialog, { cellText } from '../../common/MasterImportDialog';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getUsername = () => localStorage.getItem('username') || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const slugifyPositionCode = (name) =>
  (name || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const mapPositionMasterRow = (item) => ({
  id: item.id,
  positionName: item.roleName || item.role_name || '',
  positionCode: item.roleCode || item.role_code || '',
  status: item.status || 'ACTIVE',
  raw: item,
});

const defaultForm = {
  positionName: '',
  positionCode: '',
  status: 'ACTIVE',
};

const POSITION_IMPORT_MANDATORY = [
  { display: 'Position Name', key: 'roleName', aliases: ['Name', 'Role Name'] },
  { display: 'Position Code', key: 'roleCode', aliases: ['Code', 'Role Code'] },
];

const POSITION_IMPORT_OPTIONAL = [
  { display: 'Status', key: 'status', aliases: [] },
];

const WorkforcePositionMaster = () => {
  const { canAdd, canEdit, canDelete, canUpload } = useFunctionPermissions(FC.POSITION_MASTER);
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const tenantId = getTenantId();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [focusedField, setFocusedField] = useState('');

  const focusLabelProps = (fieldName, hasValue) => {
    const active = focusedField === fieldName;
    return {
      shrink: hasValue || active,
      sx: hasValue || active ? { bgcolor: 'background.paper', px: 0.5 } : undefined,
    };
  };

  const fieldFocus = (fieldName) => ({
    onFocus: () => setFocusedField(fieldName),
    onBlur: () => setFocusedField((current) => (current === fieldName ? '' : current)),
  });

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectionModel[0]),
    [rows, selectionModel]
  );

  const fetchPositionMasters = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      setLoading(false);
      showError('Tenant is required');
      return;
    }
    try {
      setLoading(true);
      const data = await apiFetch(
        `conductor/workforce-role-masters/by-tenant?tenant_id=${encodeURIComponent(tenantId)}`
      );
      const mapped = (Array.isArray(data) ? data : []).map(mapPositionMasterRow);
      mapped.sort((a, b) => a.positionName.localeCompare(b.positionName));
      setRows(mapped);
    } catch (err) {
      setRows([]);
      showError(err.message || 'Failed to load position master records');
    } finally {
      setLoading(false);
    }
  }, [tenantId, showError]);

  useEffect(() => {
    fetchPositionMasters();
  }, [fetchPositionMasters]);

  const columns = useMemo(
    () => [
      { field: 'positionName', headerName: 'Position Name', flex: 1, minWidth: 220, resizable: true },
      { field: 'positionCode', headerName: 'Position Code', width: 180, resizable: true },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        resizable: true,
        renderCell: (params) => ((params.value || '') === '' ? '' : String(params.value).toUpperCase()),
      },
    ],
    []
  );

  const buildPayload = () => ({
    tenantId: tenantId || null,
    roleName: form.positionName?.trim(),
    roleCode: form.positionCode?.trim() || slugifyPositionCode(form.positionName),
    status: form.status || 'ACTIVE',
    ...(mode === 'edit' ? { id: editingId } : {}),
    updatedBy: getUsername() || null,
    ...(mode === 'add' ? { createdBy: getUsername() || null } : {}),
  });

  const openAdd = () => {
    if (!canAdd) return;
    if (!tenantId) {
      showError('Tenant is required');
      return;
    }
    setMode('add');
    setEditingId(null);
    setForm({ ...defaultForm });
    setFocusedField('');
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setMode('edit');
    setEditingId(selectedRow.id);
    setFocusedField('');
    setForm({
      positionName: selectedRow.positionName || '',
      positionCode: selectedRow.positionCode || '',
      status: selectedRow.status || 'ACTIVE',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if ((mode === 'add' && !canAdd) || (mode === 'edit' && !canEdit)) return;
    if (!form.positionName?.trim()) {
      showError('Position name is required');
      return;
    }
    if (!form.positionCode?.trim() && !slugifyPositionCode(form.positionName)) {
      showError('Position code is required');
      return;
    }
    try {
      setSubmitting(true);
      const payload = buildPayload();
      if (mode === 'add') {
        await apiFetch('conductor/workforce-role-masters', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        showSuccess('Position master saved successfully');
      } else {
        await apiFetch(`conductor/workforce-role-masters/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        showSuccess('Position master updated successfully');
      }
      setDialogOpen(false);
      setSelectionModel([]);
      fetchPositionMasters();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !rowPendingDelete) return;
    try {
      setDeleteLoading(true);
      await apiFetch(`conductor/workforce-role-masters/${rowPendingDelete.id}`, { method: 'DELETE' });
      showSuccess('Record deleted successfully');
      setDeleteOpen(false);
      setRowPendingDelete(null);
      setSelectionModel([]);
      fetchPositionMasters();
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const buildPositionImportItems = ({ dataRows, columnIndexByKey }) => {
    const items = [];
    const errors = [];
    dataRows.forEach((row, index) => {
      const roleName = cellText(row, columnIndexByKey, 'roleName');
      const roleCode = cellText(row, columnIndexByKey, 'roleCode');
      if (!roleName && !roleCode) return;
      if (!roleName) {
        errors.push(`Row ${index + 2}: Position Name is required`);
        return;
      }
      if (!roleCode) {
        errors.push(`Row ${index + 2} (${roleName}): Position Code is required`);
        return;
      }
      let status = (cellText(row, columnIndexByKey, 'status') || 'ACTIVE').toUpperCase();
      if (status && status !== 'ACTIVE' && status !== 'INACTIVE') {
        errors.push(`Row ${index + 2} (${roleName}): Status must be ACTIVE or INACTIVE`);
        return;
      }
      items.push({
        tenantId,
        roleName,
        roleCode: roleCode.toUpperCase(),
        status: status || 'ACTIVE',
        createdBy: getUsername() || null,
        updatedBy: getUsername() || null,
        _rowLabel: roleName,
      });
    });
    return { items, errors };
  };

  const importPositions = async (items) => {
    if (!canUpload) return;
    if (!tenantId) {
      throw new Error('Tenant is required for import');
    }
    const failed = [];
    let successCount = 0;
    for (const item of items) {
      const { _rowLabel, ...payload } = item;
      try {
        await apiFetch('conductor/workforce-role-masters', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        successCount += 1;
      } catch (err) {
        failed.push({ rowLabel: _rowLabel || payload.roleName, error: err.message || 'Save failed' });
      }
    }
    return { successCount, failureCount: failed.length, failed };
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Position Master
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canUpload ? 'Import Positions' : 'No upload permission'}>
            <span>
              <IconButton onClick={() => { if (!canUpload) return; setImportOpen(true); }} disabled={!tenantId || !canUpload} sx={harmonatePlainIconButtonSx}>
                <UploadFileIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canEdit ? 'Edit Position' : 'No edit permission'}>
            <span>
              <IconButton onClick={openEdit} disabled={!selectedRow || !canEdit} sx={harmonatePlainIconButtonSx}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canAdd ? 'Add Position' : 'No add permission'}>
            <span>
              <IconButton onClick={openAdd} disabled={!canAdd} sx={harmonatePlainIconButtonSx}>
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete Position' : 'No delete permission'}>
            <span>
              <IconButton
                onClick={() => {
                  setRowPendingDelete(selectedRow);
                  setDeleteOpen(true);
                }}
                disabled={!selectedRow || !canDelete}
                sx={harmonatePlainIconButtonSx}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={40}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Add Position Master' : 'Edit Position Master'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <TextField
            fullWidth
            size="small"
            required
            label="Position Name"
            value={form.positionName}
            onChange={(e) => {
              const name = e.target.value;
              setForm((prev) => ({
                ...prev,
                positionName: name,
                ...(mode === 'add' && name && !prev.positionCode
                  ? { positionCode: slugifyPositionCode(name) }
                  : {}),
              }));
            }}
            InputLabelProps={focusLabelProps('positionName', Boolean(form.positionName))}
            {...fieldFocus('positionName')}
          />
          <TextField
            fullWidth
            size="small"
            required
            label="Position Code"
            value={form.positionCode}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                positionCode: e.target.value.toUpperCase(),
              }))
            }
            InputLabelProps={focusLabelProps('positionCode', Boolean(form.positionCode))}
            {...fieldFocus('positionCode')}
          />
          <TextField
            fullWidth
            size="small"
            required
            select
            label="Status"
            value={form.status}
            onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
            InputLabelProps={focusLabelProps('status', Boolean(form.status))}
            {...fieldFocus('status')}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="INACTIVE">INACTIVE</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting} sx={harmonateProceedButtonSx}>
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Delete position &quot;{rowPendingDelete?.positionName || 'selected'}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDelete} disabled={deleteLoading} sx={harmonateAbortButtonSx}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <MasterImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import positions"
        mandatoryHeaders={POSITION_IMPORT_MANDATORY}
        optionalHeaders={POSITION_IMPORT_OPTIONAL}
        dropHint="Each data row must include Position Name and Position Code. Status is optional."
        buildItems={buildPositionImportItems}
        onImport={importPositions}
        onImportSuccess={fetchPositionMasters}
        showSuccess={showSuccess}
        showError={showError}
      />

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
      />
    </Box>
  );
};

export default WorkforcePositionMaster;
